import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";

export type RowType = "aircraft" | "instructor";

/** Raison d'annulation — utile pour la télémétrie/UX */
export type CancelReason =
  | "tap" // tap court sans mouvement
  | "short" // long-press trop court
  | "moved" // mouvement horizontal avant déclenchement
  | "scroll" // scroll détecté avant déclenchement
  | "cancel" // pointercancel navigateur
  | "blocked"; // ligne saturée (aucun slot libre)

export interface CreateStartPayload {
  rowType: RowType;
  rowId: string;
  startMin: number;
  endMin: number;
}
export interface CreateChangePayload extends CreateStartPayload {}
export interface CreateCommitPayload extends CreateStartPayload {}
export interface CreateCancelPayload {
  reason?: CancelReason;
}

/**
 * Directive PRO – création de créneau par long-press (mobile/pen) ou drag immédiat (desktop).
 *
 * Caractéristiques :
 * - Anti-rebond au bord (configurable via tolerateEdgeRebound)
 * - Ancre intelligente (évite de commencer dans un créneau 1ʳᵉ position)
 * - Clamp anti-chevauchement pendant le drag (et stop autoscroll si on touche un obstacle)
 * - Nettoyage total et filets de sécu (pas d’état collant)
 */
@Directive({
  selector: "[appCreateGesture]",
  standalone: true,
})
export class CreateGestureDirective implements OnDestroy {
  /* ---------------------- Contexte temps / grille ---------------------- */
  @Input({ required: true }) open!: number; // ex: 6
  @Input({ required: true }) close!: number; // ex: 22
  @Input({ required: true }) step!: number; // ex: 15 (minutes)
  @Input({ required: true }) hourWidth!: number; // px par heure

  /* --------------------------- Cible actuelle -------------------------- */
  @Input({ required: true }) rowType!: RowType;
  @Input({ required: true }) rowId!: string;

  /* --------------- Conteneur scrollable horizontal (timeline) ---------- */
  @Input({ required: true }) sheetEl!: HTMLElement;

  /* ------------------------------- Options ----------------------------- */
  /** Délais de long-press selon pointer type */
  @Input() longPressDelayMsTouch = 250;
  @Input() longPressDelayMsPen = 180;

  /** Durée minimale du drag de création (debounce commit) */
  @Input() minCreateMs = 120;

  /** Vitesse d’autoscroll (px/frame) */
  @Input() autoScrollPxPerFrame = 8;

  /** Tolérance de mouvement avant annulation de l’armement */
  @Input() moveToleranceTouch = 10;
  @Input() moveToleranceMouse = 2;

  /** Politique au bord : true = permissif (ignore micro-rebonds), false = strict (annule) */
  @Input() tolerateEdgeRebound = true;

  /** Clamp pour empêcher de traverser la 1ʳᵉ position pendant le drag */
  @Input() clampPointerFn?: (
    rowType: RowType,
    rowId: string,
    anchor: number,
    pointer: number
  ) => number;

  /** Indique si une minute est occupée en 1ʳᵉ position (aide à choisir l’ancre) */
  @Input() isMinuteBlockedFn?: (
    rowType: RowType,
    rowId: string,
    minute: number
  ) => boolean;

  /* -------------------------------- Sorties ---------------------------- */
  @Output() selectBegin = new EventEmitter<CreateStartPayload>();
  @Output() selectChange = new EventEmitter<CreateChangePayload>();
  @Output() selectCommit = new EventEmitter<CreateCommitPayload>();
  @Output() selectCancel = new EventEmitter<CreateCancelPayload>();

  /* ------------------------------ État interne ------------------------- */
  private activePointerId: number | null = null;
  private captureEl: HTMLElement;

  private longPressT?: number;
  private longPressStartX = 0;
  private longPressStartScrollLeft = 0;

  private selecting = false;
  private mode: "none" | "create" = "none";

  private selectionAnchorMin: number | null = null;
  private lastStartMin: number | null = null;
  private lastEndMin: number | null = null;
  private selectionStartedAt = 0;
  private createMoved = false;

  private pendingCancelReason: CancelReason | undefined;

  // iOS helpers
  private creationCleanup?: () => void;
  private cancelScrollTouchMove?: (e: TouchEvent) => void;

  // autoscroll
  private autoScrollRAF: number | null = null;
  private lastAutoCtx?: {
    clientX: number;
    left: number;
    right: number;
    update: () => void;
  };

  // Anti-rebond (arm phase)
  private readonly SCROLL_ARM_TOLERANCE = 6; // px
  private readonly EDGE_EPS = 1; // px

  constructor(private host: ElementRef<HTMLElement>) {
    this.captureEl = this.host.nativeElement;
  }

  /* =============================== Lifecycle =============================== */
  ngOnDestroy(): void {
    this.forceCleanup();
  }

  /* ============================= Filets de sécu ============================ */

  @HostListener("window:blur")
  onWindowBlur() {
    this.forceCleanup();
  }

  @HostListener("document:visibilitychange")
  onVisChange() {
    if (document.visibilityState !== "visible") this.forceCleanup();
  }

  @HostListener("document:touchend", ["$event"])
  onDocTouchEnd(_ev: TouchEvent) {
    // Si on était en arm phase et que pointerup n’arrive pas, on nettoie
    if (this.longPressT || (this.activePointerId !== null && !this.selecting)) {
      this.forceCleanup();
    }
  }

  /* ================================ Handlers =============================== */

  @HostListener("contextmenu", ["$event"])
  onCtx(e: MouseEvent) {
    e.preventDefault();
  }

  /** Démarre armement (mobile/pen) ou drag immédiat (desktop). */
  @HostListener("pointerdown", ["$event"])
  onPointerDown(ev: PointerEvent) {
    // Si un ancien pointeur serait resté coincé, nettoie et continue
    if (this.activePointerId !== null) this.forceCleanup();

    if (ev.button !== 0) return;
    if (!this.sheetEl) return;
    if (this.activePointerId !== null) return;

    ev.preventDefault();
    ev.stopPropagation();

    this.activePointerId = ev.pointerId;

    // Snappe scrollLeft pour éviter subpixels
    this.longPressStartScrollLeft = Math.round(this.sheetEl.scrollLeft);
    this.longPressStartX = ev.clientX;

    // Reset état
    this.createMoved = false;
    this.mode = "none";
    this.selecting = false;
    this.selectionAnchorMin = null;
    this.lastStartMin = null;
    this.lastEndMin = null;
    this.pendingCancelReason = undefined;

    // Ancre intelligente (éviter de commencer dans un slot 1ʳᵉ position)
    const rawAnchor = this.posToMinutesFromClientX(ev.clientX);
    const anchor = this.chooseStartAnchor(rawAnchor);
    if (anchor == null) {
      this.pendingCancelReason = "blocked";
      this.selectCancel.emit({ reason: "blocked" });
      this.forceCleanup();
      return;
    }

    const isMouse = ev.pointerType === "mouse";
    const isPen = ev.pointerType === "pen";

    // Desktop : drag immédiat (UX +)
    if (isMouse) {
      try {
        this.captureEl.setPointerCapture(ev.pointerId);
      } catch {}
      this.startCreateWithAnchor(anchor);
      return;
    }

    // Mobile / Pen : long-press
    const delay = isPen ? this.longPressDelayMsPen : this.longPressDelayMsTouch;
    this.longPressT = window.setTimeout(() => {
      if (this.shouldCancelArmForScroll()) {
        this.pendingCancelReason = "scroll";
        this.selectCancel.emit({ reason: "scroll" });
        this.forceCleanup();
        return;
      }
      try {
        this.captureEl.setPointerCapture(this.activePointerId!);
      } catch {}
      this.applyIOSGestureWorkarounds(true);
      this.startCreateWithAnchor(anchor);
    }, delay);
  }

  /** Pendant armement : annule si vrai mouvement/scroll (configurable au bord). Pendant drag : met à jour la sélection. */
  @HostListener("pointermove", ["$event"])
  onPointerMove(ev: PointerEvent) {
    // Avant déclenchement : arm phase
    if (!this.selecting && this.longPressT) {
      const isMouse = ev.pointerType === "mouse";
      const tol = isMouse ? this.moveToleranceMouse : this.moveToleranceTouch;
      const dx = Math.abs(ev.clientX - this.longPressStartX);

      if (dx > tol) {
        clearTimeout(this.longPressT);
        this.longPressT = undefined;
        this.pendingCancelReason = "moved";
        this.selectCancel.emit({ reason: "moved" });
        this.forceCleanup();
        return;
      }

      if (this.shouldCancelArmForScroll()) {
        clearTimeout(this.longPressT);
        this.longPressT = undefined;
        this.pendingCancelReason = "scroll";
        this.selectCancel.emit({ reason: "scroll" });
        this.forceCleanup();
        return;
      }
      return;
    }

    // Drag de création en cours
    if (this.mode === "create" && this.selecting) {
      ev.preventDefault();
      ev.stopPropagation();

      const anchor = this.selectionAnchorMin!;
      const rawPointer = this.posToMinutesFromClientX(ev.clientX);
      const clampedPointer = this.clampPointerFn
        ? this.clampPointerFn(this.rowType, this.rowId, anchor, rawPointer)
        : rawPointer;

      // Si clamp ≠ pointer → on arrête l’autoscroll (on “colle” au bord de l’obstacle)
      if (clampedPointer !== rawPointer) this.stopAutoScroll();

      let start = this.clamp(
        Math.min(anchor, clampedPointer),
        this.open,
        this.close
      );
      let end = this.clamp(
        Math.max(anchor, clampedPointer),
        this.open,
        this.close
      );

      this.createMoved = end - start >= this.step;
      this.lastStartMin = start;
      this.lastEndMin = end;

      this.selectChange.emit({
        rowType: this.rowType,
        rowId: this.rowId,
        startMin: start,
        endMin: end,
      });

      // Autoscroll seulement si on n’est pas bloqué par clamp
      if (clampedPointer === rawPointer) {
        this.maybeAutoScroll(ev, () => {
          const raw2 = this.posToMinutesFromClientX(ev.clientX);
          const clamped2 = this.clampPointerFn
            ? this.clampPointerFn(this.rowType, this.rowId, anchor, raw2)
            : raw2;
          if (clamped2 !== raw2) {
            this.stopAutoScroll();
            return;
          }

          let s = this.clamp(Math.min(anchor, clamped2), this.open, this.close);
          let e = this.clamp(Math.max(anchor, clamped2), this.open, this.close);

          this.createMoved = e - s >= this.step;
          this.lastStartMin = s;
          this.lastEndMin = e;
          this.selectChange.emit({
            rowType: this.rowType,
            rowId: this.rowId,
            startMin: s,
            endMin: e,
          });
        });
      }
    }
  }

  /** Termine (commit) ou annule avec raison. */
  @HostListener("pointerup", ["$event"])
  onPointerUp(ev: PointerEvent) {
    if (this.activePointerId !== null && ev.pointerId !== this.activePointerId)
      return;

    ev.preventDefault();
    ev.stopPropagation();

    const longEnough = Date.now() - this.selectionStartedAt >= this.minCreateMs;

    if (
      this.mode === "create" &&
      this.selecting &&
      longEnough &&
      this.createMoved
    ) {
      this.selectCommit.emit({
        rowType: this.rowType,
        rowId: this.rowId,
        startMin: this.lastStartMin ?? 0,
        endMin: this.lastEndMin ?? 0,
      });
    } else {
      const reason: CancelReason =
        this.pendingCancelReason ??
        (this.mode === "create" && !longEnough ? "short" : "tap");
      this.selectCancel.emit({ reason });
    }

    this.forceCleanup();
  }

  @HostListener("pointercancel", ["$event"])
  onPointerCancel(_ev: PointerEvent) {
    this.selectCancel.emit({ reason: "cancel" });
    this.forceCleanup();
  }

  /* ================================ Helpers ================================ */

  /** Démarre la sélection depuis une ancre validée. */
  private startCreateWithAnchor(anchor: number) {
    this.mode = "create";
    this.selecting = true;
    this.selectionStartedAt = Date.now();
    this.selectionAnchorMin = anchor;

    // init “end” très court pour déclencher la bande
    let endInit = this.clamp(
      anchor + this.onePixelInMinutes(this.hourWidth),
      this.open,
      this.close
    );
    const endInitRaw = endInit;
    if (this.clampPointerFn) {
      endInit = this.clampPointerFn(this.rowType, this.rowId, anchor, endInit);
    }
    if (endInit !== endInitRaw) this.stopAutoScroll();

    this.lastStartMin = Math.min(anchor, endInit);
    this.lastEndMin = Math.max(anchor, endInit);

    this.selectBegin.emit({
      rowType: this.rowType,
      rowId: this.rowId,
      startMin: this.lastStartMin!,
      endMin: this.lastEndMin!,
    });
  }

  /** Vrai scroll pendant armement ? (tolère micro-rebonds selon l’option) */
  private shouldCancelArmForScroll(): boolean {
    const maxScrollNow = Math.max(
      0,
      this.sheetEl.scrollWidth - this.sheetEl.clientWidth
    );
    const atEdgeNow =
      this.sheetEl.scrollLeft <= this.EDGE_EPS ||
      this.sheetEl.scrollLeft >= maxScrollNow - this.EDGE_EPS;

    const delta = Math.abs(
      Math.round(this.sheetEl.scrollLeft) - this.longPressStartScrollLeft
    );

    // strict au bord (version “courte”) → annule dès qu’on bouge
    if (!this.tolerateEdgeRebound) return delta > this.SCROLL_ARM_TOLERANCE;

    // permissif (version “longue”) → ignore micro-rebonds si on est au bord
    return !atEdgeNow && delta > this.SCROLL_ARM_TOLERANCE;
  }

  /** Choisit une ancre libre; null si la ligne est saturée. */
  private chooseStartAnchor(rawAnchor: number): number | null {
    const anchor = this.snap(this.clamp(rawAnchor, this.open, this.close));
    if (!this.isMinuteBlockedFn) return anchor;

    if (!this.isMinuteBlockedFn(this.rowType, this.rowId, anchor))
      return anchor;

    const min = this.open * 60,
      max = this.close * 60;
    for (let m = anchor + this.step; m <= max; m += this.step) {
      if (!this.isMinuteBlockedFn(this.rowType, this.rowId, m)) return m;
    }
    for (let m = anchor - this.step; m >= min; m -= this.step) {
      if (!this.isMinuteBlockedFn(this.rowType, this.rowId, m)) return m;
    }
    return null;
  }

  /** clientX → minutes snappées et clampées sur [open;close] */
  private posToMinutesFromClientX(clientX: number) {
    const rect = this.sheetEl.getBoundingClientRect();
    const baselineLeft = rect.left;
    let xViewport = clientX - baselineLeft;
    if (xViewport < 0) xViewport = 0;

    const maxX = (this.close - this.open) * this.hourWidth;
    const x = Math.min(xViewport + this.sheetEl.scrollLeft, maxX);

    const minutesFromOpen = x * (60 / this.hourWidth);
    const minutes = this.open * 60 + minutesFromOpen;
    const snapped = this.snap(minutes);
    return Math.min(snapped, this.close * 60);
  }

  /** Lance un autoscroll doux tant qu’on est proche du bord visuel. */
  private maybeAutoScroll(ev: PointerEvent, update: () => void) {
    const rect = this.sheetEl.getBoundingClientRect();
    const EDGE = 48;
    const leftTrigger = rect.left + EDGE;
    const rightTrigger = rect.right - EDGE;

    if (ev.clientX > rightTrigger) {
      this.startAutoScroll(+1, leftTrigger, rightTrigger, update);
    } else if (ev.clientX < leftTrigger) {
      this.startAutoScroll(-1, leftTrigger, rightTrigger, update);
    } else {
      this.stopAutoScroll();
    }
  }

  private startAutoScroll(
    dir: -1 | 1,
    _left: number,
    _right: number,
    update: () => void
  ) {
    this.lastAutoCtx = { clientX: 0, left: _left, right: _right, update };
    if (this.autoScrollRAF) return;

    const tick = () => {
      if (!this.lastAutoCtx) {
        this.stopAutoScroll();
        return;
      }
      this.sheetEl.scrollLeft += dir * this.autoScrollPxPerFrame;
      this.lastAutoCtx.update();
      this.autoScrollRAF = requestAnimationFrame(tick);
    };

    this.autoScrollRAF = requestAnimationFrame(tick);
  }

  private stopAutoScroll() {
    if (this.autoScrollRAF) cancelAnimationFrame(this.autoScrollRAF);
    this.autoScrollRAF = null;
    this.lastAutoCtx = undefined;
  }

  private clamp(minute: number, openHour: number, closeHour: number) {
    const minOpen = openHour * 60,
      maxClose = closeHour * 60;
    return Math.min(maxClose, Math.max(minOpen, minute));
  }
  private onePixelInMinutes(hourW: number) {
    return 60 / hourW;
  }
  private snap(mins: number) {
    return Math.round(mins / this.step) * this.step;
  }

  /** iOS: momentum/touch-action toggling pendant le drag */
  private applyIOSGestureWorkarounds(enable: boolean) {
    try {
      const cap = this.captureEl as HTMLElement;
      if (enable) {
        const oldTA = cap.style.touchAction;
        cap.style.touchAction = "none";
        const oldMomentum = (this.sheetEl.style as any).webkitOverflowScrolling;
        (this.sheetEl.style as any).webkitOverflowScrolling = "auto";

        this.cancelScrollTouchMove = (e: TouchEvent) => e.preventDefault();
        cap.addEventListener("touchmove", this.cancelScrollTouchMove, {
          passive: false,
        });

        this.creationCleanup = () => {
          cap.style.touchAction = oldTA || "";
          cap.removeEventListener(
            "touchmove",
            this.cancelScrollTouchMove as any
          );
          (this.sheetEl.style as any).webkitOverflowScrolling =
            oldMomentum || "touch";
          this.cancelScrollTouchMove = undefined;
          this.creationCleanup = undefined;
        };
      } else {
        if (this.creationCleanup) this.creationCleanup();
      }
    } catch {}
  }

  /** Nettoyage total et sûr, quel que soit l’état courant. */
  private forceCleanup() {
    // stop autoscroll
    if (this.autoScrollRAF) cancelAnimationFrame(this.autoScrollRAF);
    this.autoScrollRAF = null;
    this.lastAutoCtx = undefined;

    // stop arm timer
    if (this.longPressT) {
      clearTimeout(this.longPressT);
      this.longPressT = undefined;
    }

    // release capture
    try {
      if (this.activePointerId != null) {
        this.captureEl.releasePointerCapture(this.activePointerId);
      }
    } catch {}

    // disable iOS tweaks
    this.applyIOSGestureWorkarounds(false);

    // reset state
    this.activePointerId = null;
    this.mode = "none";
    this.selecting = false;
    this.selectionAnchorMin = null;
    this.lastStartMin = null;
    this.lastEndMin = null;
    this.pendingCancelReason = undefined;
  }
}
